// Мінт NFT через TheoWallet: сервер готує транзакцію, ключ юзера підписує тут, у браузері.
// Приватний ключ нікуди не надсилається.
import { unlockVault } from './walletVault';
import { RELAY_URL } from './walletSend';

export const MINT_API = 'https://lc73ifzg1l.execute-api.us-east-1.amazonaws.com/default/mint-nft';
export const MINT_MIN_SOL = 0.02; // оренда акаунтів Metaplex + комісія

const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const toB64 = (bytes) => btoa(String.fromCharCode(...bytes));
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return data;
}

// Пароль/Face ID -> prepare -> перевірки -> підпис -> send -> confirm
export async function executeMint({ assetId, genesisID, fromAddress, password, keypair: unlockedKeypair, onStep, balanceSol }) {
  const step = (s) => { if (onStep) onStep(s); };
  const keypair = unlockedKeypair || await unlockVault(fromAddress, password);
  if (keypair.publicKey.toBase58() !== fromAddress) throw new Error('Wallet key mismatch');

  step('Preparing NFT (uploading to IPFS)…');
  const prep = await post(MINT_API, { action: 'prepare', assetId, genesisID });
  if (!prep.success) throw new Error(prep.error || 'Could not prepare the NFT');
  if (prep.alreadyMinted) return { alreadyMinted: true, mintAddress: prep.mintAddress };
  if (typeof balanceSol === 'number' && balanceSol < MINT_MIN_SOL) {
    throw new Error(`Not enough SOL: minting needs about ${MINT_MIN_SOL} SOL (you have ${balanceSol.toFixed(4)})`);
  }

  // Перевіряємо, що підписуємо саме те, що очікуємо
  const { VersionedTransaction } = await import('@solana/web3.js');
  const tx = VersionedTransaction.deserialize(fromB64(prep.transaction));
  const keys = tx.message.staticAccountKeys.map((k) => k.toBase58());
  const signers = keys.slice(0, tx.message.header.numRequiredSignatures);
  if (signers[0] !== fromAddress) throw new Error('Unexpected transaction: fee payer is not your wallet');
  if (!signers.includes(prep.mintAddress)) throw new Error('Unexpected transaction: mint mismatch');

  tx.sign([keypair]);

  step('Sending to Solana…');
  const sent = await post(RELAY_URL, {
    action: 'send',
    signedTx: toB64(tx.serialize()),
    blockhash: prep.blockhash,
    lastValidBlockHeight: prep.lastValidBlockHeight,
  });
  if (!sent.success) {
    const err = new Error(sent.error || 'Transaction failed');
    err.signature = sent.signature || null;
    throw err;
  }

  step('Confirming…');
  for (let i = 0; i < 8; i++) {
    const c = await post(MINT_API, { action: 'confirm', assetId, genesisID, signature: sent.signature });
    if (c.success) return { ...c, txHash: c.txHash || sent.signature };
    if (!c.pending) {
      const err = new Error(c.error || 'Could not confirm the NFT');
      err.signature = sent.signature;
      throw err;
    }
    await sleep(4000);
  }
  const err = new Error('Minting is taking longer than usual. Open the asset again in a minute.');
  err.signature = sent.signature;
  throw err;
}

// ================= ПЕРЕКАЗ NFT =================
export const NFT_TRANSFER_API = 'https://amm8jymgmc.execute-api.us-east-1.amazonaws.com/prod/transfer-nft';
const ATA_PROGRAM_ID = 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL';

// Крок 1: сервер перевіряє власність і знаходить отримувача (Genesis ID або адреса)
export async function prepareNftTransfer({ assetId, fromGenesisID, recipient }) {
  const prep = await post(NFT_TRANSFER_API, { action: 'prepare', assetId, fromGenesisID, recipient });
  if (!prep.success) throw new Error(prep.error || 'Could not prepare the transfer');
  return prep;
}

// Будуємо транзакцію самі (не довіряємо готовим байтам): токен-акаунт отримувача (idempotent) + transferChecked(1, 0)
async function buildNftTransferTx(keypair, prep) {
  const { PublicKey, Transaction, SystemProgram, TransactionInstruction, ComputeBudgetProgram } = await import('@solana/web3.js');
  const owner = keypair.publicKey;
  const mint = new PublicKey(prep.mint);
  const dest = new PublicKey(prep.toWallet);
  const prog = new PublicKey(prep.tokenProgram);
  const ataProg = new PublicKey(ATA_PROGRAM_ID);
  const ata = (o) => PublicKey.findProgramAddressSync([o.toBuffer(), prog.toBuffer(), mint.toBuffer()], ataProg)[0];
  const src = ata(owner);
  const dst = ata(dest);

  const tx = new Transaction({ feePayer: owner, blockhash: prep.blockhash, lastValidBlockHeight: prep.lastValidBlockHeight });
  // priority fee (ліміт 2M мікролампортів/CU — сервер не може «роздути» комісію)
  const priority = Math.min(Number(prep.priorityMicroLamports) || 100000, 2000000);
  tx.add(ComputeBudgetProgram.setComputeUnitLimit({ units: 60000 }));
  tx.add(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priority }));
  tx.add(new TransactionInstruction({
    programId: ataProg,
    keys: [
      { pubkey: owner, isSigner: true, isWritable: true },
      { pubkey: dst, isSigner: false, isWritable: true },
      { pubkey: dest, isSigner: false, isWritable: false },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: prog, isSigner: false, isWritable: false },
    ],
    data: Uint8Array.from([1]), // CreateIdempotent
  }));
  const data = new Uint8Array(10);
  data[0] = 12;  // TransferChecked
  data[1] = 1;   // amount = 1 (u64 LE)
  data[9] = 0;   // decimals = 0
  tx.add(new TransactionInstruction({
    programId: prog,
    keys: [
      { pubkey: src, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: dst, isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: true, isWritable: false },
    ],
    data,
  }));
  tx.sign(keypair);
  return toB64(tx.serialize());
}

// Крок 2: пароль/Face ID -> свіжий prepare -> перевірка отримувача -> підпис -> send -> confirm
export async function executeNftTransfer({ assetId, fromGenesisID, recipient, fromAddress, password, keypair: unlockedKeypair, expectedToWallet, onStep }) {
  const step = (s) => { if (onStep) onStep(s); };
  const keypair = unlockedKeypair || await unlockVault(fromAddress, password);
  if (keypair.publicKey.toBase58() !== fromAddress) throw new Error('Wallet key mismatch');

  step('Preparing transfer…');
  const prep = await prepareNftTransfer({ assetId, fromGenesisID, recipient });
  if (prep.alreadyTransferred) return prep;
  if (expectedToWallet && prep.toWallet !== expectedToWallet) throw new Error('Recipient changed. Please start again.');
  if (prep.fromWallet !== fromAddress) throw new Error('This NFT is not in your TheoWallet');

  const signedTx = await buildNftTransferTx(keypair, prep);

  step('Sending to Solana…');
  const sent = await post(RELAY_URL, { action: 'send', signedTx, blockhash: prep.blockhash, lastValidBlockHeight: prep.lastValidBlockHeight });
  if (!sent.success) {
    const err = new Error(sent.error || 'Transaction failed');
    err.signature = sent.signature || null;
    throw err;
  }

  step('Confirming…');
  for (let i = 0; i < 15; i++) {
    const c = await post(NFT_TRANSFER_API, { action: 'confirm', assetId, fromGenesisID, signature: sent.signature });
    if (c.success) return { ...c, txHash: c.txHash || sent.signature };
    if (!c.pending) {
      const err = new Error(c.error || 'Could not confirm the transfer');
      err.signature = sent.signature;
      throw err;
    }
    await sleep(4000);
  }
  const err = new Error('Transfer is taking longer than usual. Check the asset again in a minute.');
  err.signature = sent.signature;
  throw err;
}
